import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { MeetComment } from "../api/types";

/**
 * Shared team thread for a meet: team-visible comments (mat assignments,
 * logistics, "who has the tent?"). Everyone on the team can post; you can
 * delete your own (team admins can moderate any).
 */
export function MeetThread({ meetId, myUserId }: { meetId: string; myUserId?: string }) {
  const [comments, setComments] = useState<MeetComment[]>([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>();

  const load = () => {
    api
      .listComments(meetId)
      .then((d) => setComments(d.items))
      .catch((e) => setMsg((e as Error).message));
  };
  useEffect(load, [meetId]);

  const post = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    setMsg(undefined);
    try {
      await api.postComment(meetId, text);
      setBody("");
      load();
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c: MeetComment) => {
    await api.deleteComment(meetId, c.commentId, c.createdAt).catch(() => undefined);
    load();
  };

  const when = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="card">
      <h2>Team thread</h2>
      <p className="muted">
        Shared with your team — logistics, updates, questions.
      </p>

      <div className="grid" style={{ margin: "0.5rem 0" }}>
        {comments.length === 0 && (
          <p className="muted">No messages yet. Start the conversation.</p>
        )}
        {comments.map((c) => (
          <div
            key={c.commentId}
            style={{
              borderLeft: "2px solid var(--border)",
              paddingLeft: "0.6rem",
            }}
          >
            <div className="row spread">
              <strong>{c.displayName ?? "A parent"}</strong>
              <span className="muted" style={{ fontSize: "0.78rem" }}>
                {when(c.createdAt)}
                {c.userId === myUserId && (
                  <button
                    className="secondary"
                    style={{ marginLeft: "0.5rem", padding: "0.1rem 0.4rem", fontSize: "0.75rem" }}
                    onClick={() => remove(c)}
                  >
                    delete
                  </button>
                )}
              </span>
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
          </div>
        ))}
      </div>

      <textarea
        rows={2}
        placeholder="Write a message to the team…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div className="row" style={{ marginTop: "0.5rem" }}>
        <button onClick={post} disabled={busy}>
          {busy ? "Posting…" : "Post"}
        </button>
        {msg && <span className="error">{msg}</span>}
      </div>
    </div>
  );
}
